import { gql } from "@apollo/client";
import { FC } from "react";

import { useToast } from "@src/lib/hooks/useToast";
import { GraphqlOperations } from "@src/types.generated";

import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import { useDeleteChatMutation } from "./DeleteChatDialog.generated";
import { getChatTypeLabel } from "./utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chatId: string;
  directMessage?: boolean;
  onDeleted?: () => void;
};

const DeleteChatDialog: FC<Props> = ({
  open,
  onOpenChange,
  chatId,
  directMessage = false,
  onDeleted,
}) => {
  const { addToast, addErrorToast } = useToast();
  const label = getChatTypeLabel(directMessage);

  const [deleteChat, { loading }] = useDeleteChatMutation({
    variables: { input: { chatId } },
    refetchQueries: [GraphqlOperations.Query.NewMessagingChatSections],
    onError: (error) => {
      addErrorToast({ ...error, callsite: "delete_chat" });
    },
    onCompleted: (data) => {
      if (!data.deleteChat.success) {
        addErrorToast({ data, callsite: "delete_chat" });
        return;
      }
      addToast({ message: `${label} deleted` });
      onOpenChange(false);
      onDeleted?.();
    },
  });

  return (
    <ConfirmDeleteDialog
      title={`Delete ${label}`}
      body={`Deleting this ${label.toLowerCase()} removes it and all of its messages for everyone.`}
      open={open}
      onOpenChange={onOpenChange}
      loading={loading}
      onConfirm={() => deleteChat()}
    />
  );
};

gql`
  mutation DeleteChat($input: DeleteChatInput!) {
    deleteChat(input: $input) {
      success
      error {
        code
      }
    }
  }
`;

export default DeleteChatDialog;
